import React, { useState, useEffect, useRef } from 'react';
import { validateEmail } from '../../../utils/auth'; // Assuming validateEmail is here

const EmailListInput = ({
  value = [], // Array of valid emails (external source of truth)
  onChange, // Function called with array of valid emails
  placeholder = 'Enter emails separated by space or comma...',
  className = '',
}) => {
  const editableDivRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  // Track internal HTML to compare against updates from props
  const [currentHtml, setCurrentHtml] = useState('');

  // --- Helper Functions ---

  // Utility to escape HTML characters
  const escapeHtml = (unsafe) => {
    if (!unsafe) return '';
    return unsafe
         .replace(/&/g, "&amp;")
         .replace(/</g, "<")
         .replace(/>/g, ">")
         .replace(/"/g, '"')
         .replace(/'/g, "&#039;");
  }

  // Generates HTML string with styled spans for valid emails
  const generateHtmlWithTags = (text) => {
    // Regex to find potential emails OR sequences of non-whitespace/non-comma characters
    const tokenRegex = /([^\s,]+)/g;
    let html = '';
    let lastIndex = 0;
    let match;

    while ((match = tokenRegex.exec(text)) !== null) {
      const token = match[0];
      const isValidEmail = validateEmail(token);

      // Add whitespace/commas before the token
      html += text.substring(lastIndex, match.index);

      if (isValidEmail) {
        // Add styled span for valid email with a remove button (data-email attribute for identification)
        // Added pointer cursor to indicate clickability
        html += `<span style="background-color: #e0e0e0; border-radius: 4px; padding: 2px 6px; margin: 0 2px; display: inline-block; border: 1px solid #ccc; cursor: pointer;" contenteditable="false" data-email="${escapeHtml(token)}">`;
        html += escapeHtml(token);
        // Add 'x' button inside the span
        html += `<span data-remove-email="${escapeHtml(token)}" style="margin-left: 5px; color: #555; cursor: pointer; font-weight: bold;">&times;</span>`;
        html += `</span>`;
      } else {
        // Add token as plain text if not a valid email
        html += escapeHtml(token);
      }
      lastIndex = tokenRegex.lastIndex;
    }
    // Add any trailing whitespace/commas
    html += text.substring(lastIndex);
     // Add a non-breaking space at the end if the original text ended with a space,
     // to help with typing after a tag.
     if (text.endsWith(' ') || text.endsWith('\u00A0')) {
         html += '&nbsp;';
     }

    return html;
  };

  // Extracts valid emails from a text string
  const extractEmailsFromText = (text) => {
    const emailRegex = /[\w\.-]+@[\w\.-]+\.\w+/g;
    // Replace non-breaking spaces with regular spaces for regex matching
    const normalizedText = text.replace(/\u00A0/g, ' ');
    const potentialEmails = normalizedText.match(emailRegex) || [];
    return [...new Set(potentialEmails.filter(email => validateEmail(email)))];
  };

  // --- Effects ---

  // Effect to update contentEditable div when the external 'value' prop changes
  useEffect(() => {
    // Only update if not focused to avoid disrupting typing
    if (!isFocused && editableDivRef.current) {
      const textValue = value.join(' '); // Rebuild text from the validated list
      const newHtml = generateHtmlWithTags(textValue);
      // Only update DOM if HTML actually changed
      if (editableDivRef.current.innerHTML !== newHtml) {
          editableDivRef.current.innerHTML = newHtml;
          setCurrentHtml(newHtml); // Update internal tracking
      }
    }
  }, [value, isFocused]); // Rerun when the external value array changes or focus changes

  // --- Event Handlers ---

   const handleKeyDown = (event) => {
     // Prevent Enter key from creating a new line
     if (event.key === 'Enter') {
       event.preventDefault();
       // Optionally trigger email check on Enter as well?
       // handleEmailCheck();
     }
   };

  const handleKeyUp = (event) => {
    // Check if space or comma was pressed
    if (event.key === ' ' || event.key === ',') {
       handleEmailCheck();
    }
  };

  // Centralized logic to check text and update parent
  const handleEmailCheck = () => {
      if (!editableDivRef.current) return;
      const currentText = editableDivRef.current.innerText;
      const emails = extractEmailsFromText(currentText);

      // Notify parent if the list of valid emails has changed
      if (onChange) {
        const sortedValue = [...value].sort();
        const sortedEmails = [...emails].sort();
        if (JSON.stringify(sortedEmails) !== JSON.stringify(sortedValue)) {
          onChange(emails);
          // The useEffect will handle updating the innerHTML based on the new 'value' prop
        }
      }
  };

  // Handle general input changes (like backspace, delete, typing non-trigger keys)
  const handleInput = () => {
      if (!editableDivRef.current) return;
       // Update internal HTML tracking based on current rendering
       setCurrentHtml(editableDivRef.current.innerHTML);
       // Optional: Could trigger a lighter check here if needed, but might conflict
  };


  const handlePaste = (event) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);

    // Process immediately after paste
    setTimeout(() => {
      handleEmailCheck(); // Use centralized check logic
    }, 0);
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Also check emails on blur
    handleEmailCheck();
    // Force re-render from 'value' prop on blur to ensure consistency
    setTimeout(() => { // Use timeout to allow state update from handleEmailCheck first
        if (editableDivRef.current) {
            const textValue = value.join(' ');
            const html = generateHtmlWithTags(textValue);
            editableDivRef.current.innerHTML = html;
            setCurrentHtml(html); // Update internal tracking
        }
    }, 0);
  };

  // Handle clicks within the contentEditable div (for removing tags)
  const handleClick = (event) => {
    // Check if the click target is a remove button ('x')
    const removeTarget = event.target.closest('[data-remove-email]');
    if (removeTarget) {
      const emailToRemove = removeTarget.getAttribute('data-remove-email');
      if (emailToRemove && onChange) {
        const updatedEmails = value.filter(email => email !== emailToRemove);
        onChange(updatedEmails);
        // The useEffect will handle the re-render
      }
    }
  };

  return (
    <div className={`email-list-input ${className}`}>
      <div
        ref={editableDivRef}
        contentEditable="true"
        onKeyDown={handleKeyDown} // Prevent Enter
        onKeyUp={handleKeyUp} // Trigger on space/comma
        onInput={handleInput} // Handle other changes like backspace
        onPaste={handlePaste}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onClick={handleClick} // Handle clicks for removing tags
        data-placeholder={placeholder} // Use data-placeholder for CSS pseudo-element
        className="w-full pt-1 pl-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue_highlight email-input-editable" // Removed min-height, Added pl-2
        // Initialize with HTML based on initial value
        dangerouslySetInnerHTML={{ __html: generateHtmlWithTags(value.join(' ')) }}
        style={{
            whiteSpace: 'nowrap', // Prevent wrapping
            overflowX: 'auto', // Allow horizontal scrolling
            wordWrap: 'normal', // Ensure wordWrap doesn't interfere
            minHeight: 'auto', // Reset min-height or set to specific single-line height
            height: '2.5rem', // Example: Set explicit height like a standard input
         }}
      >
        {/* Content managed via innerHTML */}
      </div>
       {/* CSS for placeholder simulation */}
       <style>{`
         div[contentEditable=true].email-input-editable {
            line-height: 1.5rem; /* Adjust line-height to match height for vertical centering */
         }
         div[contentEditable=true].email-input-editable:empty:before {
           content: attr(data-placeholder);
           color: #CCCCCC;
           cursor: text;
           pointer-events: none; /* Allow clicking through placeholder */
           display: block; /* Or inline-block */
           /* Position placeholder */
           position: relative;
           top: 0.0rem; /* Adjust based on padding */
           left: 0.5rem; /* Adjust based on padding */
         }
         /* Hide scrollbar for WebKit browsers */
         div[contentEditable=true].email-input-editable::-webkit-scrollbar {
           display: none;
         }
         /* Hide scrollbar for IE, Edge and Firefox */
         div[contentEditable=true].email-input-editable {
           -ms-overflow-style: none;  /* IE and Edge */
           scrollbar-width: none;  /* Firefox */
         }
       `}</style>
   </div>
  );
};

export { EmailListInput };